import { useState, useLayoutEffect } from 'react';

/**
 * Given one or more properly formatted media queries, returns the status of each.
 *
 * @example const [greaterThan640] = useMediaQuery('(min-width: 640px)')
 */
function useMediaQuery() {
  for (var _len = arguments.length, queries = new Array(_len), _key = 0; _key < _len; _key++) {
    queries[_key] = arguments[_key];
  }
  const isSupported = typeof window !== 'undefined' && 'matchMedia' in window;
  const [{
    queryKey,
    mediaQueryLists,
    matches
  }, setState] = useState((() => {
    const queryKey = queries.join('-');
    const mediaQueryLists = queries.map(query => isSupported ? window.matchMedia(query) : null);
    const matches = mediaQueryLists.map(mediaQuerylist => !!mediaQuerylist?.matches);
    return {
      queryKey,
      mediaQueryLists,
      matches
    };
  })() // IIFE because `matches` depends on `mediaQueryLists`
  );
  useLayoutEffect(() => {
    const mediaQueryListToIndex = {};
    mediaQueryLists.forEach((mediaQueryList, index) => {
      if (mediaQueryList !== null) {
        mediaQueryListToIndex[mediaQueryList.media] = index;
      }
    });

    // update return shape if queries changes
    if (queries.join('-') !== queryKey) {
      const newKey = queries.join('-');
      const newMediaQueryLists = queries.map(query => isSupported ? window.matchMedia(query) : null);
      const newMatches = newMediaQueryLists.map(mediaQuerylist => !!mediaQuerylist?.matches);
      setState({
        queryKey: newKey,
        mediaQueryLists: newMediaQueryLists,
        matches: newMatches
      });
    }
    const listener = e => {
      setState(prevState => {
        const nextMatches = prevState.matches.slice();
        nextMatches[mediaQueryListToIndex[e.media]] = e.matches;
        return {
          ...prevState,
          matches: nextMatches
        };
      });
    };
    mediaQueryLists.forEach(mediaQueryList => {
      mediaQueryList?.addEventListener('change', listener);
    });
    return () => {
      mediaQueryLists.forEach(mediaQueryList => {
        mediaQueryList?.removeEventListener('change', listener);
      });
    };
  }, [queries, isSupported, queryKey, mediaQueryLists, setState]);
  return matches;
}

export { useMediaQuery };
